import { useContext, useEffect, useState } from "react";

// Services
import useCountryAxiosInstance from "../../../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../../../ui-components/Notifications";
import CountryConfigContext from "../../../CountryConfigContext";
import { FormatNumberOptions, useIntl } from "react-intl";

// Components
import KpiWorksiteBarChart from "../../../components/Charts/KpiWorksiteBarChart";
import KpiWorksiteDonut from "../../../components/Charts/KpiWorksiteDonut";
import WorksiteTrendChart from "./WorksiteTrendChart";
import UsersHeatMap from "../Users/UsersHeatMap";
import WorksitesDetailTable from "./DetailTable";

//Ui
import SearchSelect from "../../../ui-components/SearchSelect";
import { PageSection } from "../../../ui-components/Container";
import Placeholder from "../../../ui-components/Placeholder";
import { COLORS, lostWorksiteColors } from "../../../components/Charts/commons";
import Toggle from "../../../ui-components/Toggle";
import Input from "../../../ui-components/Input";
import { WorksiteTerminatedFilter } from "../WorksiteTerminatedFilter";

// Types
import {
  AnalyticsWorksitesProps,
  ChartWorksite,
  WorksiteRow,
  TrendData,
  GeoData,
  LostWorksiteData,
} from "./Worksites.type";
import {
  WorksiteOwnershipType,
} from "../Analytics.type"
import SankeyChart from "./SankeyChart";

export const statusOptions = ["planning", "design", "open", "closed"];
const ownershipOptions = [
  "creator",
  "promoter",
  "from_internal",
  "from_external",
];
const currencyOption: FormatNumberOptions = {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
};
const dimensionOrder = {
  planning: 1,
  design: 2,
  open: 3,
  closed: 4,
  promoter: 5,
  creator: 6,
  from_internal: 7,
  from_external: 8,
};

export function AnalyticsWorksites({
  visibilityUserIds,
  analysisPeriod,
  internalUsers,
  worksiteTypes,
  worksitesOwnershipFilter
}: AnalyticsWorksitesProps) {
  const intl = useIntl();
  const { push } = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();

  const [worksites, setWorksites] = useState<WorksiteRow[] | undefined>(undefined);
  const [worksiteDimension, setWorksiteDimension] = useState<"status" | "ownership">("status");
  const [chartWorksites, setChartWorksites] = useState<ChartWorksite[] | undefined>(undefined);

  const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: string }[]>([]);
  const [selectedOwnership, setSelectedOwnerships] = useState<{ label: string; value: string }[]>([]);

  const [timeUnit, setTimeUnit] = useState<string>("month");
  const [trend, setTrend] = useState<TrendData[] | undefined>(undefined);
  const [geo, setGeo] = useState<GeoData[] | undefined>(undefined);
  const [sankey, setSankey] = useState<object | undefined>(undefined);
  const [lostWorksiteData, setLostWorksiteData] = useState<LostWorksiteData[] | undefined>([]);
  const [totalLostWorksite, setTotalLostWorksite] = useState<number | undefined>(0);

  const [filterAddress, setFilterAddress] = useState<string | undefined>(undefined);
  const [filterCity, setFilterCity] = useState<string | undefined>(undefined);
  const [filterProvince, setFilterProvince] = useState<string | undefined>(undefined);
  const [filterWorksiteType, setFilterWorksiteType] = useState([]);
  const [filterServices, setFilterServices] = useState([]);
  const [worksiteTerminationFilter, setWorksiteTerminationFilter] = useState<string | undefined>(undefined);
  const [worksiteTerminationReasonFilter, setWorksiteTerminationReasonFilter] = useState<string | undefined>(undefined);
  const { config } = useContext(CountryConfigContext);

  // Per-chart loading states
  const [kpiLoading, setKpiLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(true);
  const [sankeyLoading, setSankeyLoading] = useState(true);
  const [lostLoading, setLostLoading] = useState(true);

  const services = (config.services as { active: boolean; name: string }[])
    .filter((i) => i.active)
    .map((i) => i.name)
    .sort();

  useEffect(() => {
    if (worksites) {
      setChartWorksites(
        worksites
          .map((i) => ({
            category: intl.formatMessage({ id: i.dimension }),
            value: i.val_worksites,
            amount: i.amt_worksites,
            order: dimensionOrder[i.dimension],
            settings: { fill: COLORS[i.dimension], stroke: "rgb(255,255,255)" },
          }))
          .sort((a, b) => a.order - b.order)
      );
    }
  }, [worksites]);

  // Builds the base payload shared by all endpoints (no dimension field)
  const buildBasePayload = () => {
    const payload: any = {
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_ids: visibilityUserIds,
      internal_users: internalUsers.map((i) => i.id),
      filter_status: selectedStatus.length ? selectedStatus.map((i) => i.value) : undefined,
      filter_ownership: selectedOwnership.length ? selectedOwnership.map((i) => i.value) : undefined,
      worksites_ownership: worksitesOwnershipFilter,
      ...(worksiteTerminationFilter ? { worksite_termination: worksiteTerminationFilter } : {}),
      ...(worksiteTerminationReasonFilter ? { worksite_termination_reason: worksiteTerminationReasonFilter } : {}),
    };
    if (filterAddress) payload.address = filterAddress;
    if (filterCity) payload.city = filterCity;
    if (filterProvince) payload.cod_province = filterProvince.toUpperCase();
    if (filterWorksiteType) payload.cod_worksite_type = filterWorksiteType.map((i: any) => i.value);
    if (filterServices) payload.services = filterServices.map((i: any) => i.value);
    return payload;
  };

  // Effect 1: KPI charts — re-fires when dimension toggles too
  useEffect(() => {
    const fetchKpi = async () => {
      try {
        setKpiLoading(true);
        const response = await countryApiRequest.post("/analytics/worksites/kpi", { // @ts-ignore
          ...buildBasePayload(),
          dimension: worksiteDimension,
        });
        // @ts-ignore
        setWorksites(response.worksites);
      } catch (error) {
        console.log(error);
        setWorksites([]);
        push({ title: intl.formatMessage({ id: "server_error" }), type: "error" });
      } finally {
        setKpiLoading(false);
      }
    };
    fetchKpi();
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    visibilityUserIds, analysisPeriod, worksiteDimension,
    selectedOwnership, selectedStatus,
    filterAddress, filterCity, filterProvince, filterWorksiteType, filterServices,
    worksiteTerminationFilter, worksiteTerminationReasonFilter,
  ]);

  // Effect 2: trend, geo, sankey, lost — independent, do NOT re-fire on dimension toggle
  useEffect(() => {
    const basePayload = buildBasePayload();

    setTrendLoading(true);
    setGeoLoading(true);
    setSankeyLoading(true);
    setLostLoading(true);

    // @ts-ignore
    countryApiRequest.post("/analytics/worksites/trend", basePayload)
      .then((response: any) => {
        setTrend(response.trend);
        setTimeUnit(response.time_unit);
      })
      .catch((error: any) => console.log(error))
      .finally(() => setTrendLoading(false));

    // @ts-ignore
    countryApiRequest.post("/analytics/worksites/geo", basePayload)
      .then((response: any) => setGeo(response.geo))
      .catch((error: any) => console.log(error))
      .finally(() => setGeoLoading(false));

    // @ts-ignore
    countryApiRequest.post("/analytics/worksites/sankey", basePayload)
      .then((response: any) => setSankey(response))
      .catch((error: any) => console.log(error))
      .finally(() => setSankeyLoading(false));

    // @ts-ignore
    countryApiRequest.post("/analytics/worksites/lostWorksites", basePayload)
      .then((response: any) => {
        setLostWorksiteData(
          response.map((el: LostWorksiteData) => ({
            value: el.value || "unknown",
            category: intl.formatMessage({ id: el.category || "unknown" }),
            settings: {
              fill: lostWorksiteColors[el.category || "unknown"],
              stroke: "rgb(255,255,255)",
            },
          }))
        );
        setTotalLostWorksite(response.length);
      })
      .catch((error: any) => console.log(error))
      .finally(() => setLostLoading(false));
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    visibilityUserIds, analysisPeriod,
    selectedOwnership, selectedStatus,
    filterAddress, filterCity, filterProvince, filterWorksiteType, filterServices,
    worksiteTerminationFilter, worksiteTerminationReasonFilter,
  ]);

  return (
    <div className="w-full mt-5">
      <div className="flex">
        <div className="flex flex-row gap-3 w-full mb-3">
          <SearchSelect
            label={"Stato"}
            options={statusOptions.map((i) => ({
              value: i,
              label: intl.formatMessage({ id: i }),
            }))}
            onChange={(e: any) => setSelectedStatus(e)}
            value={selectedStatus}
            isMulti={true}
            CustomGroup={undefined}
            loadOptions={undefined}
            CustomOption={undefined}
          />
          <SearchSelect
            label="Ownership"
            options={ownershipOptions.map((i) => ({
              value: i,
              label: intl.formatMessage({ id: i }),
            }))}
            onChange={(e: any) => setSelectedOwnerships(e)}
            value={selectedOwnership}
            isMulti={true}
            CustomGroup={undefined}
            loadOptions={undefined}
            CustomOption={undefined}
          />
        </div>
      </div>
      <h2 className="mb-3">
        {intl.formatMessage({
          id: "worksite_filters",
          defaultMessage: "Filtri di anagrafica cantiere",
        })}
      </h2>
      <div className="flex">
        <div>
          <Input
            label={intl.formatMessage({ id: "address", defaultMessage: "Indirizzo" })}
            value={filterAddress}
            onChange={(e: any) => setFilterAddress(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <Input
            label={intl.formatMessage({ id: "city", defaultMessage: "Città" })}
            value={filterCity}
            onChange={(e: any) => setFilterCity(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <Input
            label={intl.formatMessage({ id: "province", defaultMessage: "Provincia" })}
            value={filterProvince}
            onChange={(e: any) => setFilterProvince(e.target.value)}
          />
        </div>
        <div className="ml-4">
          <SearchSelect
            label={intl.formatMessage({ id: "worksite_type", defaultMessage: "Tipo cantiere" })}
            value={filterWorksiteType}
            onChange={(e: any) => setFilterWorksiteType(e)}
            options={worksiteTypes?.map((i) => ({
              value: i.cod_worksite_type,
              label: i.des_worksite_type,
            })) ?? []}
            isMulti
          />
        </div>
        <div className="ml-4">
          <SearchSelect
            label={intl.formatMessage({ id: "configurations", defaultMessage: "Configurazioni" })}
            value={filterServices}
            options={config.services.map((el) => ({
              value: el.name,
              label: el.name,
            }))}
            onChange={(e: any) => setFilterServices(e)}
            isMulti
          />
        </div>
        <div className="ml-4">
          <WorksiteTerminatedFilter
            worksiteTerminationFilter={worksiteTerminationFilter}
            setWorksiteTerminationFilter={setWorksiteTerminationFilter}
            worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
            setWorksiteTerminationReasonFilter={setWorksiteTerminationReasonFilter}
          />
        </div>
      </div>

      <div>
        <PageSection
          title={<span className="font-bold">{intl.formatMessage({ id: "worksites" })}</span>}
          description={undefined}
        >
          <div className="flex flex-row gap-x-2 font-bolder mb-8">
            <span>Stato</span>
            <div className="-mt-5">
              {/*@ts-ignore*/}
              <Toggle
                checked={worksiteDimension === "ownership"}
                onChange={() => {
                  setWorksiteDimension(worksiteDimension === "status" ? "ownership" : "status");
                }}
              />
            </div>
            <span>Ownership</span>
          </div>

          {kpiLoading || !chartWorksites ? (
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 lg:gap-x-4 gap-y-4">
              <Placeholder height="h-60" />
              <Placeholder height="h-60" />
            </div>
          ) : chartWorksites.length ? (
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 lg:gap-x-4 gap-y-4">
              <div>
                <KpiWorksiteDonut id="kpiWorksiteDonut" data={chartWorksites} intl={intl} />
              </div>
              <div>
                <div>
                  <span className="text-lg text-gray-500 font-bold m-1">
                    {intl.formatMessage({ id: "total_amount_kk" })}
                  </span>
                  <span className="text-lg text-gray-700 font-bold m-1">
                    {intl.formatNumber(
                      chartWorksites.reduce((acc, i) => acc + i.amount, 0),
                      currencyOption
                    )}
                  </span>
                </div>
                {chartWorksites.reduce((acc, i) => acc + i.amount, 0) > 0 ? (
                  <KpiWorksiteBarChart id="kpiWorksiteBarChart" data={chartWorksites} intl={intl} />
                ) : null}
              </div>
            </div>
          ) : (
            <span className="text-lg text-gray-500 my-32">
              {intl.formatMessage({ id: "no_data_available" })}
            </span>
          )}

          <div className="w-full flex flex-col lg:flex-row my-4 items-center">
            <div className="w-full lg:w-2/3">
              {trendLoading || !trend ? (
                <div className="p-8"><Placeholder height="h-60" /></div>
              ) : (
                <div>
                  <WorksiteTrendChart
                    id={"worksiteTrendChart"}
                    data={trend}
                    intl={intl}
                    timeUnit={timeUnit}
                  />
                </div>
              )}
            </div>
            <div className="w-full lg:w-1/3">
              {geoLoading || !geo ? (
                <div className="p-8"><Placeholder height="h-60" /></div>
              ) : (
                <UsersHeatMap
                  id="UsersHeatMap"
                  data={geo.map((i) => ({
                    value: i.num_worksites,
                    id: "IT-" + i.cod_province,
                  }))}
                />
              )}
            </div>
          </div>
        </PageSection>

        <div className="flex flex-row grid grid-cols-3">
          <div className="col-span-2">
            {sankeyLoading ? (
              <div className="p-8"><Placeholder height="h-96" /></div>
            ) : (
              <SankeyChart id="kpiWorksiteSankey" data={sankey} intl={intl} />
            )}
          </div>
          <div className="col-span-1">
            <h4 className="flex justify-between items-center text-lg leading-6 font-medium text-black-500">
              <span>
                {intl.formatMessage({
                  id: "lost_worksites_reasons",
                  defaultMessage: "Cantieri persi: motivazioni",
                })}
              </span>
            </h4>
            {lostLoading ? (
              <Placeholder height="h-60" />
            ) : (
              <KpiWorksiteDonut id="kpiLostWorksiteDonut" data={lostWorksiteData} intl={intl} />
            )}
          </div>
        </div>

        <div className="mt-8">
          {!worksiteTypes ? <Placeholder height="h-60" /> : <WorksitesDetailTable
            filtering={false}
            intl={intl}
            push={push}
            countryApiRequest={countryApiRequest}
            analysisPeriod={analysisPeriod}
            internalUsers={internalUsers}
            selectedStatus={selectedStatus}
            selectedOwnership={selectedOwnership}
            visibilityUserIds={visibilityUserIds}
            worksiteTypes={worksiteTypes}
            services={services}
            address={filterAddress}
            city={filterCity}
            cod_province={filterProvince}
            cod_worksite_type={filterWorksiteType.map((i: any) => i.value)}
            filterServices={filterServices.map((i: any) => i.value)}
            worksitesOwnershipFilter={worksitesOwnershipFilter}
            worksiteTerminationFilter={worksiteTerminationFilter}
            worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
          />}
        </div>
      </div>
    </div>
  );
}
